/**
 * Pure helpers for the selected driver's path and next stops on the Live Monitor map.
 */

import type { Route, RouteStop, TrackPoint } from '../../types';
import { isValidCoordinate } from '../../utils/coordinates';

// GPS silent this long → break the line. 10 min: prod 2 Nov, 6% of app send gaps exceed it; ~5 min gaps are a stationary heartbeat, so <= 5 min would split at every stop.
export const GAP_MS = 10 * 60_000;

const DONE: ReadonlySet<RouteStop['status']> = new Set(['completed', 'skipped', 'failed']);

export const isDone = (stop: RouteStop): boolean => DONE.has(stop.status);

/** The GPS trail as line pieces of [lng, lat], split wherever the app went quiet for longer than `gapMs`. */
export function pathLines(trail: TrackPoint[], gapMs = GAP_MS): [number, number][][] {
  const lines: [number, number][][] = [];
  let prevAt = -Infinity;
  for (const p of trail) {
    if (!isValidCoordinate(p.lat, p.lng)) continue;
    const at = Date.parse(p.recorded_at);
    if (Number.isNaN(at)) continue;
    if (lines.length === 0 || at - prevAt > gapMs) lines.push([]);
    lines[lines.length - 1].push([p.lng, p.lat]);
    prevAt = at;
  }
  return lines.filter((line) => line.length > 1);
}

/**
 * Today's stops for the driver, in driving order: the route he is on first, then the rest.
 * Drafts are not given to the driver yet; depot rows carry no task, so their status never moves.
 */
export function driverStops(routes: Array<Route & { stops?: RouteStop[] }>): RouteStop[] {
  return routes
    .filter((r) => r.status !== 'draft' && r.status !== 'cancelled')
    .sort((a, b) => Number(b.status === 'in_progress') - Number(a.status === 'in_progress'))
    .flatMap((r) => (r.stops ?? []).filter((s) => s.type !== 'depot' && isValidCoordinate(s.lat, s.lng)));
}
